import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { take } from 'rxjs/operators';
import { DestroyBase } from '../components/common/ng-destroy-base';
import { AppServiceHelperService } from './app-service-helper.service';
import { EventBusService } from './event-bus.service';
import { TranslateService } from '@ngx-translate/core';
import { CldrIntlService, IntlService } from '@progress/kendo-angular-intl';
import { ReportDesignerTranslatorService } from '../modules/report-designer/services/report-designer-translator.service';
import { AppSettingsService } from './app-settings.service';
import { environment } from '../../environments/environment';

/**
 * This service coordinates checking out Report Designer widgets for exclusive editing by a specific user.
 */
@Injectable()
export class AppLocalizationService extends DestroyBase {
  /**
   * The app locale
   */
  public appLocale: string = null;

  /**
   * The current Dictionary config
   */
  public currentDictionaryConfig: any = null;

  /**
   * Constructor
   * @param appSettings AppSettingsService
   * @param reportDesignerTranslator ReportDesignerTranslatorService
   * @param translateService TranslateService
   * @param eventBus EventBusService
   * @param http HttpClient
   * @param appServiceHelper AppServiceHelperService
   * @param localeId LOCALE_ID
   * @param intlService IntlService
   */
  constructor(private appSettings: AppSettingsService,
    private reportDesignerTranslator: ReportDesignerTranslatorService,
    private translateService: TranslateService,
    private eventBus: EventBusService,
    private http: HttpClient,
    private appServiceHelper: AppServiceHelperService,
    @Inject(LOCALE_ID) public localeId: string,
    public intlService: IntlService) {
    super();
  }

  /**
   * Initiializes the service and loads all data
   * @returns
   */
  public init(): Promise<void> {
    return new Promise<void>((resolve) => {
      const url = this.appServiceHelper.getServiceEndpoint('pollux.dataverse.api', 'dictionary/list');

      this.http.get(url).subscribe((locales: any[]) => {
        this.appSettings.availableReportDesignerLocales = [];

        for (const locale of locales) {
          this.appSettings.availableReportDesignerLocales.push(locale);
        }

        this.setLocale(environment.defaultLocale, false);
        resolve();
      });
    });
  }

  /**
   * Applies the locale settings.
   * @param localeScriptDesignator The locale in script designator notation, separated by '-' (en-US)
   */
  private applyLocale(localeScriptDesignator: string): void {
    this.appLocale = localeScriptDesignator; //The application uses the script designator notation ('de-DE')

    //Angular
    this.translateService.use(localeScriptDesignator);

    //Kendo
    this.localeId = localeScriptDesignator;
    (<CldrIntlService>this.intlService).localeId = localeScriptDesignator;

    this.eventBus.broadcastEvent("localeChanged", localeScriptDesignator);
  }

  /**
   * Sets the application locale for the currently logged in user. Please note that the Report Designer's document area is not affected by this setting because this depends on the report/template settings.
   * As this operation also sets the locale for all Kendo UI components, these should be used carefully in any Report Designer widgets if they use locale-specific templates.
   * 
   * <b>Warning</b> This is an asynchronous operation. Therefore, all further operations that might depend on the application's locale should only be performed when the returned promise was resolved or rejected.
   * @param newLocale The new locale that will be set for the current user. Should be in script designator notation (separated by '-')
   * @param suppressStore  If set to true, the value will not be written back to the database. Set it to false or leave it out to save the settings.
   * @returns A Promise to wait for
   */
  public setLocale(newLocale: string, suppressStore: boolean): Promise<void> {
    const localeScriptDesignator = newLocale.split('_').join('-'); //separated by '-', so replace all occurrences of '_' by '-' -> de-DE

    return new Promise<void>((resolve, _reject) => {

      if (!suppressStore) {
        //Also store the value in the user's settings.
        const url = this.appServiceHelper.getServiceEndpoint('pollux.api', `language?locale=${localeScriptDesignator}`);
        this.http.post(url, {}).pipe(take(1)).subscribe(() => {
          this.applyLocale(localeScriptDesignator);
          resolve();
        });
      }
      else {
        this.applyLocale(localeScriptDesignator);
        resolve();
      }
    });
  }

  /**
   * Gets the application locale
   * @returns The application locale
   */
  public getLocale(): string {
    return this.appLocale;
  }

  /**
   * Sets all language configurations   * 
   * @param configurations Language configurations
   */
  public setAllReportDesignerDictionaryConfigurations(configurations: any[]) {
    this.appSettings.allReportDesignerLocales = configurations || [];
  }

  /**
   * Sets the available language configurations   * 
   * @param configurations Language configurations
   * @param configurations Should we set current config to first in array of configs
   */
  public setReportDesignerDictionaryConfigurations(configurations: any[], setCurrentConfigToFirst: boolean) {
    if (configurations?.length) {
      this.currentDictionaryConfig = null;//reset
      this.appSettings.availableReportDesignerLocales = configurations;
      if (setCurrentConfigToFirst) {
        const firstConfig = configurations[0];
        this.setReportDesignerDictionaryConfigById(firstConfig.id);
      }
    }
  }

  /**
   * Sets the dictionary info in the Report Designer document area.   * 
   * @param localeString The new dictionary info in the Report Designer document area
   */
  public setReportDesignerDictionaryConfigById(dictionaryConfigId: number): Promise<void> {
    const newDictionaryConfigId = dictionaryConfigId;
    if (!this.currentDictionaryConfig || this.currentDictionaryConfig.id !== newDictionaryConfigId) {
      return new Promise<void>((resolve) => {
        const newDictionaryConfig = this.appSettings.availableReportDesignerLocales.find(l => l.id === newDictionaryConfigId);
        this.currentDictionaryConfig = newDictionaryConfig;

        //since we choose to have 1 dictionary per locale (not using dict-0001, etc anymore).. take locale instead of name
        const dictionaryLocale = this.currentDictionaryConfig.locale;

        const localeScriptDesignator = dictionaryLocale.split('_').join('-'); //separated by '-', so replace all occurrences of '_' by '-' -> de-DE

        this.reportDesignerTranslator.setLocale(localeScriptDesignator);

        this.reportDesignerTranslator.ready('all').then(() => {
          //inform some change occured, so app can react (f.e. adjust dropdown languages in RD)
          this.eventBus.broadcastEvent('rd:dictionaryConfigIdSet');
          this.eventBus.broadcastEvent('reportDesignerLocaleChanged', localeScriptDesignator);

          resolve();
        });
      });

    }
    else {
      //inform some change occured, so app can react (f.e. adjust dropdown languages in RD)
      this.eventBus.broadcastEvent('rd:dictionaryConfigIdSet');

      //we need to return promise
      return this.reportDesignerTranslator.ready('all');
    }
  }

  /**
  * Gets the dictionary info in the Report Designer document area.  * 
  * @returns The current dictionary info in the Report Designer document area
  */
  public getReportDesignerDictionaryConfig(): any {
    if (this.currentDictionaryConfig === null) {
      const currentLocale = this.reportDesignerTranslator.getLocale();
      this.currentDictionaryConfig = this.appSettings.availableReportDesignerLocales.find(l => l.name === currentLocale);
    }

    return this.currentDictionaryConfig;
  }

  /**
   * Gets the decimals separator for the current RD locale
   * @returns
   */
  public getReportDesignerDecimalsSeparator(): string {
    const dictionaryConfig = this.getReportDesignerDictionaryConfig();
    return dictionaryConfig.decimalSeparator;
  }

  /**
   * Gets the thousands separator for the current RD locale
   * @returns
   */
  public getReportDesignerThousandsSeparator(): string {
    const dictionaryConfig = this.getReportDesignerDictionaryConfig();
    return dictionaryConfig.thousandsSeparator;
  }

  /**
  * @returns The locale button list for the Report Designer toolbar
  */
  public getReportDesignerLocalizationToolbarButtons(): any[] {
    const ret = [];

    for (const dictConfig of this.appSettings.availableReportDesignerLocales) {
      const text = dictConfig.description;
      const hidden = false;
      ret.push({ type: "button", id: "buttonToggleLocale" + dictConfig.id, text: text, enable: true, hidden: hidden });
    }

    return ret;
  }

  /**
  * Toggles the report designer locale. Deprecated
  */
  public toggleReportDesignerLocale(toolbar) {
    const dictionaryConfig = this.getReportDesignerDictionaryConfig();

    let nextLocaleIndex = this.appSettings.availableReportDesignerLocales.indexOf(dictionaryConfig) + 1;
    nextLocaleIndex %= this.appSettings.availableReportDesignerLocales.length;

    const nextDictionaryConfig = this.appSettings.availableReportDesignerLocales[nextLocaleIndex];
    this.setReportDesignerDictionaryConfigById(nextDictionaryConfig.id).then(() => {
      for (const dictConfig of this.appSettings.availableReportDesignerLocales) {
        if (nextDictionaryConfig.id !== dictConfig.id) {
          toolbar.hide("#buttonToggleLocale" + dictConfig.id);
        }
        else {
          toolbar.show("#buttonToggleLocale" + dictConfig.id);
        }
      }
    });
  }
}
